// Sentry Institute Management App Color Palette
// Based on Sentry color palette - exact colors from design system

const colors = {
  // Primary Brand Colors
  primary: {
    50: '#eff6ff',
    100: '#dbeafe',
    200: '#bfdbfe',
    300: '#93c5fd',
    400: '#60a5fa',
    500: '#3b82f6',
    600: '#2563eb', // Primary button color - exact from Sentry
    700: '#1d4ed8', // Primary button hover - exact from Sentry
    800: '#1e40af',
    900: '#1e3a8a',
  },

  // Sidebar Colors - exact from Sentry palette
  sidebar: {
    background: '#0f172a', // Background - exact from Sentry
    active: '#1e293b', // Active - exact from Sentry
    text: '#cbd5e1', // Icons secondary - exact from Sentry
    textActive: '#ffffff',
    textMuted: '#94a3b8',
    border: '#334155', // Divider - exact from Sentry
    hover: '#1e293b',
    icons: '#38bdf8', // Sky blue accent - exact from Sentry
  },

  // Page Layout - exact from Sentry palette
  page: {
    background: '#f8fafc', // Page BG - exact from Sentry
    backgroundAlt: '#f1f5f9', // Header - exact from Sentry
  },

  // Card Components - exact from Sentry palette
  card: {
    background: '#ffffff', // Card BG - exact from Sentry
    border: '#e2e8f0', // Card Border - exact from Sentry
    shadow: 'rgba(0, 0, 0, 0.08)',
    radius: '12px',
  },

  // Status Colors - exact from Sentry palette
  status: {
    success: '#16a34a', // Success - exact from Sentry
    successLight: '#dcfce7',
    warning: '#f59e0b', // Warning - exact from Sentry
    warningLight: '#fef3c7',
    error: '#dc2626', // Error - exact from Sentry
    errorLight: '#fee2e2',
    info: '#0284c7', // Info - exact from Sentry
    infoLight: '#e0f2fe', // Hover - exact from Sentry
  },

  // Button Colors - exact from Sentry palette
  button: {
    primary: '#2563eb', // Primary Button - exact from Sentry
    primaryHover: '#1d4ed8', // Primary Button hover - exact from Sentry
    secondary: '#e5e7eb', // Secondary Button - exact from Sentry
    secondaryHover: '#d1d5db', // Secondary Button hover - exact from Sentry
    danger: '#dc2626',
    dangerHover: '#b91c1c',
    success: '#16a34a',
    successHover: '#15803d',
    warning: '#f59e0b',
    warningHover: '#d97706',
  },

  // Table/Data Display - exact from Sentry palette
  table: {
    header: '#f1f5f9', // Header - exact from Sentry
    headerText: '#475569',
    border: '#e2e8f0',
    rowHover: '#e0f2fe', // Hover - exact from Sentry
    rowSelected: '#bae6fd', // Selected - exact from Sentry
    rowBorder: '#f0f0f0',
  },

  // Form Elements - exact from Sentry palette
  form: {
    border: '#e2e8f0', // Card border - exact from Sentry
    borderFocus: '#2563eb', // Input Border - exact from Sentry
    background: '#ffffff',
    placeholder: '#9ca3af',
    label: '#1e293b', // Active text - exact from Sentry
    error: '#dc2626',
  },

  // Text Colors - exact from Sentry palette
  text: {
    primary: '#1e293b', // Active text - exact from Sentry
    secondary: '#475569',
    muted: '#64748b',
    light: '#d1d5db',
    white: '#ffffff',
    accent: '#2563eb',
  },

  // Icon Colors
  icon: {
    primary: '#38bdf8', // Sky blue accent
    secondary: '#cbd5e1',
    muted: '#9ca3af',
    success: '#16a34a',
    warning: '#f59e0b',
    error: '#dc2626',
    info: '#0284c7',
  },

  // Alert/Notification Colors
  alert: {
    success: {
      background: '#dcfce7',
      border: '#16a34a',
      text: '#166534',
    },
    warning: {
      background: '#fef3c7',
      border: '#f59e0b',
      text: '#92400e',
    },
    error: {
      background: '#fee2e2',
      border: '#dc2626',
      text: '#991b1b',
    },
    info: {
      background: '#e0f2fe',
      border: '#0284c7',
      text: '#0c4a6e',
    },
  },

  // Utility Colors
  utility: {
    white: '#ffffff',
    black: '#000000',
    transparent: 'transparent',
    divider: '#e2e8f0',
    backdrop: 'rgba(0, 0, 0, 0.5)',
    overlay: 'rgba(0, 0, 0, 0.25)',
  },

  // Gradients
  gradient: {
    primary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    success: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
    warning: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    error: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
    info: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
  },

  // Dark Mode (for future implementation)
  dark: {
    background: '#111827',
    surface: '#1f2937',
    text: '#f9fafb',
    textSecondary: '#d1d5db',
    border: '#374151',
  },
};

// Color utility functions
export const getStatusColor = (status) => {
  const statusMap = {
    success: colors.status.success,
    warning: colors.status.warning,
    error: colors.status.error,
    info: colors.status.info,
    active: colors.status.success,
    inactive: colors.text.muted,
    pending: colors.status.warning,
    completed: colors.status.success,
    cancelled: colors.status.error,
  };
  return statusMap[status] || colors.text.muted;
};

export const getButtonColor = (variant) => {
  const variantMap = {
    primary: colors.button.primary,
    secondary: colors.button.secondary,
    success: colors.button.success,
    warning: colors.button.warning,
    danger: colors.button.danger,
  };
  return variantMap[variant] || colors.button.primary;
};

export const rgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export default colors;
